import React, { useEffect, useState } from 'react';
import {
  AccordionContent,
  AccordionListItem,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TruncatedText,
} from '@ais-ui/ui-core';
import { Group, ReportTemplate, useGetAllGroups } from '@ais-ui/websockets';
import { PiQuestion } from 'react-icons/pi';
import { cn } from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';
import { reportTypeToIcon } from './reportsTypes';
import { ReportTemplateDelete } from './Mutations/DeleteReportTemplate';
import { ScheduleReports } from './Mutations/ScheduleReportTemplate';
import { ReportScheduleDelete } from './Mutations/DeleteReportSchedule';
import { ShareReport } from './Mutations/ShareReport/ShareReport';
import { getGroupsFromIds } from './utils';
import { ShowLastReportButton } from './ShowLastReportButton';
import { ReportItem } from './ReportItem';
import { ReportTemplateDropDownMenu } from './ReportTemplateDropDownMenu';
import { ReportStatusIcons } from './ReportStatusIcons';

interface ReportTemplateItemProps {
  reportTemplate: ReportTemplate;
}

export const ReportTemplateItem = React.memo(
  ({ reportTemplate }: ReportTemplateItemProps) => {
    const { messages } = useLocalization();
    const [reportTemplateGroups, setReportTemplateGroups] = useState<Group[]>(
      [],
    );
    const { data: groups } = useGetAllGroups();

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showScheduleDialog, setShowScheduleDialog] = useState(false);
    const [showDeleteScheduleDialog, setShowDeleteScheduleDialog] =
      useState(false);
    const [showShareDialog, setShowShareDialog] = useState(false);

    const onDelete = () => setShowDeleteDialog(true);
    const onSchedule = () => setShowScheduleDialog(true);
    const onDeleteSchedule = () => setShowDeleteScheduleDialog(true);
    const onShare = () => setShowShareDialog(true);

    useEffect(() => {
      if (groups) {
        setReportTemplateGroups(getGroupsFromIds(reportTemplate, groups));
      }
    }, [groups, reportTemplate]);

    const availableJobs = reportTemplate.jobs.filter((report) => report.uuid);
    const successfulJobs = availableJobs.filter((report) => report.status === 'FINISHED');

    const ReportIconComponent = Object.prototype.hasOwnProperty.call(
      reportTypeToIcon,
      reportTemplate.type,
    )
      ? reportTypeToIcon[reportTemplate.type]
      : PiQuestion;

    const isScheduled =
      reportTemplate.isScheduled &&
      reportTemplate.schedule?.interval !== 'disabled';

    let statusMessage;
    switch (reportTemplate.tplneweststatus) {
      case 'FINISHED':
        statusMessage = messages.reportsTemplateItemLastReportSuccess;
        break;
      case 'FAILED':
        statusMessage = messages.reportsTemplateItemLastReportFailed;
        break;
      case 'NONE':
        statusMessage = messages.reportsTemplateItemNoReports;
        break;
      case 'EXECUTING':
        statusMessage = messages.reportsTemplateItemReportIsExecuting;
        break;
      case 'PENDING':
        statusMessage = messages.reportsTemplateItemReportIsPending;
        break;
      case 'FINISHED_SCHEDULED':
        statusMessage = messages.reportsTemplateItemReportFinishedSchedule;
        break;
      case 'ABORTED':
        statusMessage = messages.reportsTemplateItemLastReportAborted;
        break;
      default:
        statusMessage = '';
        break;
    }

    return (
      <AccordionListItem
        id={`report-template-item-${reportTemplate.tplName}`}
        key={reportTemplate.tplUuid}
        value={reportTemplate.tplUuid}
        className="pr-2"
        kebabMenuWidth={50}
        actionButtons={
          <>
            <ShowLastReportButton
              report={
                successfulJobs && successfulJobs.length > 0
                  ? successfulJobs[successfulJobs.length - 1]
                  : undefined
              }
              reportTemplate={reportTemplate}
            />
            <Separator orientation="vertical" className="h-6 bg-border" />
          </>
        }
        triggerContent={
          <div className="flex items-center gap-1 flex-1 min-w-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex-shrink-0">
                  <ReportIconComponent
                    className={cn(
                      'size-4 group-hover/report-type-item:text-primary',
                      reportTemplate.tplneweststatus === 'FINISHED' &&
                        'text-success',
                      reportTemplate.tplneweststatus === 'FAILED' &&
                        'text-destructive',
                      reportTemplate.tplneweststatus === 'EXECUTING' &&
                        'text-blue-600',
                      reportTemplate.tplneweststatus === 'PENDING' &&
                        'text-muted-foreground',
                      reportTemplate.tplneweststatus === 'ABORTED' &&
                        'text-orange-400',
                    )}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>{statusMessage}</TooltipContent>
            </Tooltip>
            <TruncatedText
              text={reportTemplate.tplName}
              className="text-sm text-left font-medium leading-none group-hover:underline"
              maxWidth="160px"
            />
            {availableJobs.length > 0 && (
              <span className="text-xs text-muted-foreground shrink-0">
                ({availableJobs.length})
              </span>
            )}
          </div>
        }
        kebabMenu={
          <div className="flex items-center">
            <ReportStatusIcons
              groups={reportTemplateGroups}
              reportTemplate={reportTemplate}
            />
            <ReportTemplateDropDownMenu
              reportTemplate={reportTemplate}
              onDelete={onDelete}
              onSchedule={onSchedule}
              onDeleteSchedule={onDeleteSchedule}
              onShare={onShare}
              isScheduled={isScheduled}
            />
          </div>
        }
      >
        <AccordionContent className="px-2 pb-2">
          <div className="border rounded-md overflow-hidden">
            {availableJobs.length === 0 && (
              <div className="px-4 py-3 text-sm text-muted-foreground">
                {messages.reportsTemplateItemReportsEmptyValue}
              </div>
            )}
            {[...availableJobs].reverse().map((report) => (
              <ReportItem
                key={report.uuid}
                report={report}
                reportTemplate={reportTemplate}
              />
            ))}
          </div>
        </AccordionContent>

        {showDeleteDialog && (
          <ReportTemplateDelete
            reportTemplate={reportTemplate}
            showDialog={showDeleteDialog}
            setShowDialog={setShowDeleteDialog}
          />
        )}
        {!isScheduled && showScheduleDialog && (
          <ScheduleReports
            reportTemplate={reportTemplate}
            showDialog={showScheduleDialog}
            setShowDialog={setShowScheduleDialog}
          />
        )}
        {isScheduled && (
          <ReportScheduleDelete
            reportTemplate={reportTemplate}
            showDialog={showDeleteScheduleDialog}
            setShowDialog={setShowDeleteScheduleDialog}
          />
        )}
        {showShareDialog && (
          <ShareReport
            reportTemplate={reportTemplate}
            open={showShareDialog}
            onOpenChange={setShowShareDialog}
          />
        )}
      </AccordionListItem>
    );
  },
);
